import {
    ArrowRightIcon,
    CheckIcon,
    FileStackIcon,
    FileTextIcon,
    FolderOpenIcon,
    PlusIcon,
    UploadIcon,
    XIcon,
} from "lucide-react";
import { useMemo, useState, type ComponentType, type ReactNode } from "react";

import { IScenarioObject } from "@/api/agents/agents.types";
import { getStarterQuestionText } from "@/api/agents/starterQuestions";
import { IAttachmentOption } from "@/api/attachments/attachments.types";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ChatUploadModal } from "@/pages/frontend/chat/components/composer/ChatUploadModal";
import { LibrarySelectionModal } from "@/pages/frontend/chat/components/composer/LibrarySelectionModal";

import { SlotView, UseInputGate, useInputGate } from "./useInputGate";

/**
 * The prop shape both composer pickers share. Injectable so Storybook and tests
 * can substitute lightweight stubs for the MUI + network-backed real modals.
 */
type PickerComponent = ComponentType<{
    open: boolean;
    onClose: () => void;
    onConfirm: (opts: IAttachmentOption[]) => void;
}>;

interface InputGateModalProps {
    open: boolean;
    /** The slotted scenario whose documents this gate collects. */
    scenario: IScenarioObject;
    /** Receives the collected documents, in load-bearing order, on start. */
    onStart: (collected: IAttachmentOption[]) => void;
    /** Called once the gate is dismissed (after any discard confirmation). */
    onClose: () => void;
    /** Library source picker; defaults to the real composer modal. */
    LibraryPicker?: PickerComponent;
    /** Upload source picker; defaults to the real composer modal. */
    UploadPicker?: PickerComponent;
}

const StatusIndicator = ({ satisfied }: { satisfied: boolean }) =>
    satisfied ? (
        <span
            aria-hidden
            className="bg-success text-success-foreground animate-in zoom-in-50 flex size-5 shrink-0 items-center justify-center rounded-full duration-200 motion-reduce:animate-none"
        >
            <CheckIcon className="size-3" />
        </span>
    ) : (
        <span
            aria-hidden
            className="border-muted-foreground/40 size-5 shrink-0 rounded-full border-2"
        />
    );

/** The Library/Upload source choice; `children` is the DropdownMenuTrigger. */
const SourceMenu = ({
    gate,
    index,
    children,
}: {
    gate: UseInputGate;
    index: number;
    children: ReactNode;
}) => (
    <DropdownMenu>
        {children}
        <DropdownMenuContent align="end" className="min-w-52">
            <DropdownMenuItem onClick={() => gate.openPicker(index, "library")}>
                <FolderOpenIcon />
                Choose from Library
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => gate.openPicker(index, "upload")}>
                <UploadIcon />
                Upload a document
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
);

const SlotRow = ({
    view,
    gate,
    LibraryPicker,
    UploadPicker,
}: {
    view: SlotView;
    gate: UseInputGate;
    LibraryPicker: PickerComponent;
    UploadPicker: PickerComponent;
}) => {
    const { slot, index, docs, satisfied, canAddMore } = view;
    const isSingle = slot.max_count === 1;

    const sourceLink = (label: string) => (
        <SourceMenu gate={gate} index={index}>
            <DropdownMenuTrigger
                className={cn(
                    buttonVariants({ variant: "link", size: "sm" }),
                    "cursor-pointer"
                )}
            >
                {label}
            </DropdownMenuTrigger>
        </SourceMenu>
    );

    return (
        <div
            role="group"
            aria-label={view.accessibleName}
            className="border-border bg-card flex flex-col gap-2 rounded-lg border p-3"
        >
            <div className="flex items-center gap-2">
                <StatusIndicator satisfied={satisfied} />
                <span className="text-foreground text-sm font-medium">
                    {slot.label}
                </span>
                {slot.min_count > 1 && !satisfied && (
                    <span className="text-muted-foreground text-xs tabular-nums">
                        {`${docs.length} of ${slot.min_count}`}
                    </span>
                )}
                {!slot.required && (
                    <Badge
                        variant="secondary"
                        className="bg-muted text-muted-foreground ml-auto"
                    >
                        Optional
                    </Badge>
                )}
            </div>

            {docs.length === 0 ? (
                <SourceMenu gate={gate} index={index}>
                    <DropdownMenuTrigger className="group/add border-muted-foreground/30 hover:border-primary/40 hover:bg-muted/40 focus-visible:ring-ring/50 flex w-full cursor-pointer items-center gap-3 rounded-md border border-dashed px-3 py-2.5 text-left transition-colors outline-none focus-visible:ring-2">
                        <PlusIcon
                            aria-hidden
                            className="text-muted-foreground group-hover/add:text-primary size-4 shrink-0 transition-colors"
                        />
                        <span className="min-w-0 flex-1">
                            <span className="text-foreground block text-sm font-medium">
                                Add document
                            </span>
                            <span className="text-muted-foreground block text-xs">
                                Choose from your library or upload from your
                                computer
                            </span>
                        </span>
                    </DropdownMenuTrigger>
                </SourceMenu>
            ) : (
                <div className="flex flex-col gap-2">
                    {docs.map((doc, d) => (
                        <div
                            key={`${doc.attachment_id}-${d}`}
                            className="bg-muted/40 animate-in fade-in slide-in-from-top-1 flex items-center gap-3 rounded-md px-3 py-2 duration-200 motion-reduce:animate-none"
                        >
                            <span
                                aria-hidden
                                className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-md"
                            >
                                <FileTextIcon className="size-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p
                                    title={doc.attachment_name}
                                    className="text-foreground truncate text-sm font-medium"
                                >
                                    {doc.attachment_name}
                                </p>
                                {doc.attachment_subtitle && (
                                    <p
                                        title={doc.attachment_subtitle}
                                        className="text-muted-foreground truncate text-xs"
                                    >
                                        {doc.attachment_subtitle}
                                    </p>
                                )}
                            </div>
                            {isSingle && sourceLink("Change")}
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-muted-foreground hover:text-foreground"
                                aria-label={`Remove ${doc.attachment_name}`}
                                onClick={() => gate.removeDoc(index, d)}
                            >
                                <XIcon />
                            </Button>
                        </div>
                    ))}
                    {!isSingle && canAddMore && (
                        <div className="flex justify-end">
                            {sourceLink("Add another")}
                        </div>
                    )}
                </div>
            )}

            <LibraryPicker
                open={view.libraryOpen}
                onClose={gate.closePicker}
                onConfirm={(opts) => gate.addDocs(index, opts)}
            />
            <UploadPicker
                open={view.uploadOpen}
                onClose={gate.closePicker}
                onConfirm={(opts) => gate.addDocs(index, opts)}
            />
        </div>
    );
};

/**
 * Owns per-open collection state, so mount it per scenario (conditional render
 * or `key={scenario.question}`) rather than swapping `scenario` on a persistent
 * instance.
 */
export const InputGateModal = ({
    open,
    scenario,
    onStart,
    onClose,
    LibraryPicker = LibrarySelectionModal,
    UploadPicker = ChatUploadModal,
}: InputGateModalProps) => {
    const gate = useInputGate(scenario.input_slots);
    const [confirmingDiscard, setConfirmingDiscard] = useState(false);

    // Editing while the discard prompt shows withdraws the question — the
    // broker has answered it by continuing.
    const rowGate = useMemo<UseInputGate>(
        () => ({
            ...gate,
            openPicker: (slotIndex, source) => {
                setConfirmingDiscard(false);
                gate.openPicker(slotIndex, source);
            },
            removeDoc: (slotIndex, docIndex) => {
                setConfirmingDiscard(false);
                gate.removeDoc(slotIndex, docIndex);
            },
        }),
        [gate]
    );

    const remaining = gate.totalRequiredDocs - gate.readyRequiredDocs;
    const pct =
        gate.totalRequiredDocs === 0
            ? 0
            : (gate.readyRequiredDocs / gate.totalRequiredDocs) * 100;

    const requestClose = () => {
        if (gate.hasAnyDocs) {
            setConfirmingDiscard(true);
        } else {
            onClose();
        }
    };

    const discard = () => {
        gate.reset();
        setConfirmingDiscard(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) requestClose();
            }}
        >
            <DialogContent className="flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
                <div className="flex flex-col gap-3 border-b p-4">
                    <div className="flex flex-col gap-1 pr-8">
                        <span className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase">
                            <FileStackIcon aria-hidden className="size-3.5" />
                            Before you start
                        </span>
                        <DialogTitle className="line-clamp-2 text-lg leading-snug font-semibold tracking-tight">
                            {getStarterQuestionText(scenario)}
                        </DialogTitle>
                    </div>
                    {gate.totalRequiredDocs > 0 && (
                        <div className="flex flex-col gap-1.5">
                            <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
                                <div
                                    className={cn(
                                        "h-full rounded-full transition-[width,background-color] duration-300 ease-out motion-reduce:transition-none",
                                        gate.allRequiredSatisfied
                                            ? "bg-success"
                                            : "bg-primary"
                                    )}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <div
                                role="status"
                                aria-live="polite"
                                className="text-right"
                            >
                                <span
                                    className={cn(
                                        "text-muted-foreground text-sm transition-colors",
                                        gate.allRequiredSatisfied &&
                                            "text-success font-medium"
                                    )}
                                >
                                    {`${gate.readyRequiredDocs} of ${gate.totalRequiredDocs} ready`}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
                    {gate.slots.map((view) => (
                        <SlotRow
                            key={view.index}
                            view={view}
                            gate={rowGate}
                            LibraryPicker={LibraryPicker}
                            UploadPicker={UploadPicker}
                        />
                    ))}
                </div>

                {confirmingDiscard ? (
                    <div className="bg-muted/50 flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-foreground text-sm">
                            Discard the documents you&apos;ve added?
                        </span>
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button
                                variant="ghost"
                                onClick={() => setConfirmingDiscard(false)}
                            >
                                Keep editing
                            </Button>
                            <Button variant="destructive" onClick={discard}>
                                Discard
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-muted/50 flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center">
                        {!gate.allRequiredSatisfied && (
                            <span
                                id="input-gate-start-hint"
                                className="text-muted-foreground text-sm"
                            >
                                {`${remaining} more ${
                                    remaining === 1 ? "document" : "documents"
                                } needed`}
                            </span>
                        )}
                        <div className="flex flex-col-reverse gap-2 sm:ml-auto sm:flex-row">
                            <Button
                                variant="ghost"
                                className="text-muted-foreground"
                                onClick={requestClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={
                                    !gate.allRequiredSatisfied ||
                                    gate.anyPickerOpen
                                }
                                aria-describedby={
                                    gate.allRequiredSatisfied
                                        ? undefined
                                        : "input-gate-start-hint"
                                }
                                className="group/start hover:bg-primary/90"
                                onClick={() => onStart(gate.allCollectedFlat)}
                            >
                                Start
                                <ArrowRightIcon className="transition-transform duration-200 group-hover/start:translate-x-0.5 motion-reduce:transition-none" />
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
